import React, { useState } from 'react';
import { Image, ImageSourcePropType, TouchableOpacity, View } from 'react-native';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import { ScrollView } from 'react-native-web-hover';
import CustomButton from '../components/CustomButton';
import MyAppText from '../components/MyAppText';
import UserInformationModal from '../components/UserInformationModal';
import { useAuth } from '../context/Auth';
import { translate } from '../services/translate';
import { WindowInformation } from '../services/window-information';
import { useCurrentApp } from '../context/CurrentApp';
import { applicationPages, RootNavigation } from '../Routes';

interface MenuComponentProps {
    navigation: RootNavigation;
}

interface MenuItem {
    action: () => void;
    icon: ImageSourcePropType;
    text: string;
    disabled: boolean;
}

export default function Menu({ navigation }: MenuComponentProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const [showDropDown, setShowDropDown] = useState(false);
    const { user: authenticatedUser } = useAuth();
    const currentApp = useCurrentApp();

    const menuItems: MenuItem[] = [];

    for (const moduleObj of Object.values(applicationPages.modules)) {
        if (!moduleObj.hideFromMenu && moduleObj.canAccess(authenticatedUser, currentApp)) {
            menuItems.push({
                action: () => moduleObj.navigate(navigation),
                icon: moduleObj.icon,
                text: translate(moduleObj.label),
                disabled: !Object.values(moduleObj.screens).some(screen => screen.canAccess(authenticatedUser, currentApp))
            });
        }
    }

    return (
        <>
            <View style={styles.container}>
                <View style={{ paddingHorizontal: windowInfo.width < 580 ? 10 : 80 }}>
                    {authenticatedUser ?
                        <MyAppText style={styles.welcomeText}>{translate('welcome') + ', ' + (authenticatedUser.role ? `${authenticatedUser.role.name} ` : '') + (authenticatedUser.warName || authenticatedUser.name) + (authenticatedUser.actingBodyUnit?.name ? ' - ' + authenticatedUser.actingBodyUnit?.name : '') + '.'}</MyAppText>
                        : <></>
                    }
                </View>
                <ScrollView contentContainerStyle={styles.scrollContent(windowInfo.width < 580)}>
                    {menuItems.filter(item => !item.disabled).map((item, itemIndex) => (
                        <TouchableOpacity key={itemIndex} onPress={item.action} style={styles.card(windowInfo.width)}>
                            <View style={styles.iconBox}>
                                <Image source={item.icon} style={styles.icon} />
                            </View>
                            <View style={styles.textContent}>
                                <MyAppText style={styles.text}>{item.text}</MyAppText>
                                <CustomButton onPress={item.action} text={translate('starting')} type='secondary' />
                            </View>
                        </TouchableOpacity>
                    ))}
                </ScrollView>
            </View>
            <UserInformationModal
                showDropDown={showDropDown}
                setShowDropDown={setShowDropDown}
                navigation={navigation}
            />
        </>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        rowGap: 40,
        paddingTop: 40
    },
    welcomeText: {
        fontSize: 24,
        fontWeight: 'bold',
        color: theme.colors.labelColor
    },
    card: (width: number) => ({
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        flexDirection: 'row',
        alignItems: 'center',
        padding: 15,
        height: 90,
        gap: 15,
        width: width < 406 ? 335 : 406,
        borderWidth: 1,
        borderColor: theme.colors.container.border
    }),
    iconBox: {
        height: 54,
        width: 54,
        borderRadius: 4,
        backgroundColor: UnistylesRuntime.themeName === 'light' ? '#000028' : '#222426',
        justifyContent: 'center',
        alignItems: 'center'
    },
    icon: {
        height: 36,
        width: 36,
        objectFit: 'contain',
    },
    text: {
        color: theme.colors.labelColor,
        fontSize: 16,
        fontWeight: 'bold',
        whiteSpace: 'break-spaces'
    },
    scrollContent: (smallPadding: boolean) => ({
        paddingHorizontal: smallPadding ? 10 : 80,
        rowGap: 20,
        columnGap: 16,
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'flex-start'
    }),
    textContent: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
        justifyContent: 'space-between'
    }
}));
